import { z } from 'zod';
export const artifactClaimKindSchema = z.enum([
    'database_schema_addition',
    'database_schema_modification',
    'api_endpoint_addition',
    'api_endpoint_modification',
    'worker_or_queue_addition',
    'external_dependency_addition',
    'architectural_pattern_reuse',
    'ui_surface_addition',
    'product_behavior',
    'operational_behavior',
    'other',
]);
export const artifactEvidenceSourceKindSchema = z.enum([
    'repository_file',
    'prior_approved_specification',
    'prior_approved_technical_plan',
    'prior_approved_test_plan',
    'prior_approved_tasks',
    'governance_document',
    'ticket_context',
]);
export const artifactSurfaceDispositionSchema = z.enum(['new', 'changed', 'existing']);
export const artifactClaimSchema = z
    .object({
    claim_id: z
        .string()
        .min(1)
        .max(128)
        .regex(/^[A-Za-z0-9][A-Za-z0-9._:-]{0,127}$/),
    claim_text: z.string().min(1).max(2048),
    claim_kind: artifactClaimKindSchema,
    artifact_section: z.string().min(1).max(512),
})
    .strict();
export const artifactEvidenceItemSchema = z
    .object({
    evidence_id: z
        .string()
        .min(1)
        .max(128)
        .regex(/^[A-Za-z0-9][A-Za-z0-9._:-]{0,127}$/),
    source_kind: artifactEvidenceSourceKindSchema,
    locator: z.string().min(1).max(512),
    summary: z.string().min(1).max(1024),
    retrieved_at: z.string().datetime().optional(),
})
    .strict();
export const artifactClaimEvidenceLinkSchema = z
    .object({
    claim_id: z.string().min(1).max(128),
    evidence_id: z.string().min(1).max(128),
    support_level: z.enum(['direct', 'analogous', 'none_unsupported']),
    notes: z.string().max(1024).optional(),
})
    .strict();
export const artifactNewOrChangedSurfaceSchema = z
    .object({
    surface_kind: z.string().min(1).max(128),
    locator: z.string().min(1).max(512),
    disposition: artifactSurfaceDispositionSchema,
    notes: z.string().max(1024).optional(),
})
    .strict();
export const artifactUncertaintyOptionSchema = z
    .object({
    option_id: z
        .string()
        .min(1)
        .max(128)
        .regex(/^[A-Za-z0-9][A-Za-z0-9._:-]{0,127}$/),
    label: z.string().min(1).max(1024),
})
    .strict();
export const artifactUncertaintySchema = z
    .object({
    uncertainty_id: z
        .string()
        .min(1)
        .max(128)
        .regex(/^[A-Za-z0-9][A-Za-z0-9._:-]{0,127}$/)
        .optional(),
    text: z.string().min(1).max(2048),
    impact: z.string().min(1).max(1024),
    related_claim_ids: z.array(z.string().max(128)).max(32).optional(),
    /** Optional SpecKit-style concrete choices for guided review (2–3). */
    options: z.array(artifactUncertaintyOptionSchema).min(2).max(3).optional(),
    /** Must match one `options[].option_id` when `options` is present. */
    recommended_option_id: z
        .string()
        .min(1)
        .max(128)
        .regex(/^[A-Za-z0-9][A-Za-z0-9._:-]{0,127}$/)
        .optional(),
    /** Section heading for UI deep-link (gateway may inject). */
    artifact_section: z.string().max(512).optional(),
    /**
     * Episode question id for this uncertainty. Stamped when a disagreement
     * question is kept, so guided review can resume the clarification pause
     * without comparing wording. The panel id stays `uncertainty_id`.
     */
    clarification_question_id: z.string().uuid().optional(),
})
    .strict()
    .superRefine((val, ctx) => {
    if (val.options === undefined) {
        if (val.recommended_option_id !== undefined) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: 'recommended_option_id requires options',
                path: ['recommended_option_id'],
            });
        }
        return;
    }
    if (val.recommended_option_id === undefined) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'recommended_option_id is required when options are present',
            path: ['recommended_option_id'],
        });
        return;
    }
    const optionIds = new Set();
    for (const [index, option] of val.options.entries()) {
        if (optionIds.has(option.option_id)) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: 'options[].option_id values must be unique',
                path: ['options', index, 'option_id'],
            });
        }
        optionIds.add(option.option_id);
    }
    if (!val.options.some((option) => option.option_id === val.recommended_option_id)) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'recommended_option_id must match an options[].option_id',
            path: ['recommended_option_id'],
        });
    }
});
/** Canonical claim-evidence manifest for SDD artifact generation (profile-agnostic). */
export const artifactClaimEvidenceSchema = z
    .object({
    schema_version: z.literal(1),
    claims: z.array(artifactClaimSchema).max(256),
    evidence: z.array(artifactEvidenceItemSchema).max(256),
    claim_evidence_links: z.array(artifactClaimEvidenceLinkSchema).max(512),
    new_or_changed_surfaces: z.array(artifactNewOrChangedSurfaceSchema).max(128).optional(),
    uncertainties: z.array(artifactUncertaintySchema).max(64).optional(),
})
    .strict()
    .superRefine((val, ctx) => {
    const uncertaintyIds = new Set();
    for (const [index, uncertainty] of (val.uncertainties ?? []).entries()) {
        if (!uncertainty.uncertainty_id)
            continue;
        if (uncertaintyIds.has(uncertainty.uncertainty_id)) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: 'uncertainty_id values must be unique',
                path: ['uncertainties', index, 'uncertainty_id'],
            });
        }
        uncertaintyIds.add(uncertainty.uncertainty_id);
    }
});
export const artifactReviewFindingSchema = z
    .object({
    severity: z.enum(['critical', 'warning', 'advisory']),
    claim_id: z.string().max(128).nullable().optional(),
    evidence_refs: z.array(z.string().max(128)).max(32).default([]),
    issue: z.string().min(1).max(2048),
    recommendation: z.string().min(1).max(2048),
})
    .strict();
export const artifactReviewVerdictSchema = z
    .object({
    schema_version: z.literal(1),
    verdict: z.enum(['pass', 'revision_required', 'clarification_required']),
    review_confidence: z.enum(['high', 'medium', 'low']),
    required_revision_summary: z.string().max(4096).nullable().optional(),
    findings: z.array(artifactReviewFindingSchema).max(64),
})
    .strict();
export function buildArtifactGroundingSummary(metadata) {
    if (metadata === null || metadata === undefined || typeof metadata !== 'object') {
        return null;
    }
    const record = metadata;
    const evidenceParsed = artifactClaimEvidenceSchema.safeParse(record['artifact_claim_evidence']);
    const reviewParsed = artifactReviewVerdictSchema.safeParse(record['artifact_review']);
    const evidence = evidenceParsed.success ? evidenceParsed.data : undefined;
    const review = reviewParsed.success ? reviewParsed.data : undefined;
    if (evidence === undefined && review === undefined) {
        return null;
    }
    return {
        claimCount: evidence?.claims.length ?? 0,
        evidenceCount: evidence?.evidence.length ?? 0,
        uncertaintyCount: evidence?.uncertainties?.length ?? 0,
        reviewVerdict: review?.verdict ?? null,
        blockingFindingCount: review?.findings.filter((finding) => finding.severity === 'critical').length ?? 0,
    };
}
const MANAGED_ARTIFACT_METADATA_KEYS = [
    'spec_agent_metadata',
    'tech_plan_agent_metadata',
    'task_agent_metadata',
    'test_plan_agent_metadata',
];
/** Read `artifact_claim_evidence` from any managed artifact assistant payload. */
export function extractArtifactClaimEvidenceFromPayload(payload) {
    if (typeof payload !== 'object' || payload === null) {
        return null;
    }
    const record = payload;
    for (const key of MANAGED_ARTIFACT_METADATA_KEYS) {
        const meta = record[key];
        if (typeof meta !== 'object' || meta === null) {
            continue;
        }
        const raw = meta['artifact_claim_evidence'];
        const parsed = artifactClaimEvidenceSchema.safeParse(raw);
        if (parsed.success) {
            return parsed.data;
        }
    }
    return null;
}
export function extractArtifactReviewFromPayload(payload) {
    if (typeof payload !== 'object' || payload === null) {
        return null;
    }
    const record = payload;
    for (const key of MANAGED_ARTIFACT_METADATA_KEYS) {
        const meta = record[key];
        if (typeof meta !== 'object' || meta === null) {
            continue;
        }
        const parsed = artifactReviewVerdictSchema.safeParse(meta['artifact_review']);
        if (parsed.success) {
            return parsed.data;
        }
    }
    return null;
}
export function attachArtifactReviewToPayload(payload, review) {
    if (typeof payload !== 'object' || payload === null) {
        return payload;
    }
    const record = { ...payload };
    for (const key of MANAGED_ARTIFACT_METADATA_KEYS) {
        const meta = record[key];
        if (typeof meta !== 'object' || meta === null) {
            continue;
        }
        record[key] = {
            ...meta,
            artifact_review: review,
        };
        return record;
    }
    return payload;
}
//# sourceMappingURL=artifact-claim-evidence.schema.js.map